
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Users,
  Calendar,
  BarChart3,
  Settings,
  GraduationCap,
  FileText,
  Image,
} from "lucide-react";

const Navigation = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Improved regex patterns for more accurate route matching
  const isStudentPage = /^\/students?\/?(?:new|edit|[0-9]+|payment|exams)?/.test(currentPath);
  const isTeacherPage = /^\/teachers?\/?(?:new|edit|[0-9]+|overview|payment|timetable)?/.test(currentPath);
  const isTimetablePage = /^\/timetable(?:\/.*)?$|^\/attendance(?:\/.*)?$/.test(currentPath);
  const isReportsPage = /^\/reports?(?:\/.*)?$/.test(currentPath);
  const isSettingsPage = /^\/settings?\//.test(currentPath) || currentPath === "/settings";
  const isCertificatesPage = currentPath.includes('/certificate-templates');

  return (
    <nav className="space-y-1 py-2 min-w-[200px]">
      <NavItem 
        to="/" 
        icon={<Home size={18} className="text-white" />} 
        label="Home" 
        active={currentPath === "/" || currentPath === "/dashboard" || currentPath === "/home"}
      />
      
      <NavItem 
        to="/students" 
        icon={<GraduationCap size={18} className="text-white" />} 
        label="Students" 
        active={isStudentPage}
      />
      
      <NavItem 
        to="/teachers" 
        icon={<Users size={18} className="text-white" />} 
        label="Teachers" 
        active={isTeacherPage} 
      />

      <NavItem 
        to="/timetable" 
        icon={<Calendar size={18} className="text-white" />} 
        label="Timetable" 
        active={isTimetablePage}
      />
      
      <NavItem 
        to="/reports" 
        icon={<BarChart3 size={18} className="text-white" />} 
        label="Reports" 
        active={isReportsPage}
      />
      
      <NavItem 
        to="/certificate-templates" 
        icon={<Image size={18} className="text-white" />} 
        label="Certificate Templates" 
        active={isCertificatesPage}
      />
      
      <NavItem 
        to="/settings" 
        icon={<Settings size={18} className="text-white" />} 
        label="Settings" 
        active={isSettingsPage}
      />
    </nav>
  );
};

// Updated NavItem component with more focused types and consistent styling
interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}

const NavItem = ({ to, icon, label, active }: NavItemProps) => {
  const location = useLocation();
  const isActive = active !== undefined ? active : location.pathname === to;

  return (
    <NavLink
      to={to}
      className={
        isActive
          ? "flex items-center py-2 px-3 rounded-md transition-colors bg-[#33C3F0] text-white"
          : "flex items-center py-2 px-3 rounded-md transition-colors text-muted-foreground hover:bg-[#D3E4FD] hover:text-[#1EAEDB]"
      }
      aria-current={isActive ? "page" : undefined}
    >
      <span className="mr-2">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
};

export default Navigation;
